import React, { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';

import { TableBody, TableContainer, TableHead, TablePagination, TableRow } from '@mui/material';
import TableMui from '@mui/material/Table';
import { PAGE_INITIAL_VALUE, ROWS_PER_PAGE_OPTION } from 'common/constants';
import { formatCPF } from 'utils/formatters';
import { useStoreSelector } from 'v3/data/store';
import { setFilterCurrentPage, setFilterRowsPerPage } from 'v3/data/store/onlineContracting/actions';
import { getOriginSlug } from 'v3/data/store/onlineContracting/mapping';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import { RoutesEnum } from 'v3/routes/enum';
import { OnlineContractingMobileTable } from 'v3/shared/components/MobileTable/OnlineContractingMobileTable';
import { NoItemsFound } from 'v3/shared/components/NoItemsFound';
import { FinancingStatus } from 'v3/shared/components/Status/FinancingStatus';
import { getChannelLabel } from 'v3/shared/components/Table/OnlineContractingTable/statusHelpers';
import {
  OnlineTableCell,
  OnlineTableCellGroup,
  PaperStyled,
  StatusTableCellGroup,
  TablePaginationStyles,
  TableRowStyled,
} from 'v3/shared/components/Table/styles';
import { SubtitleMedium } from 'v3/shared/typography/SubtitleMedium';
import { TitleMedium } from 'v3/shared/typography/TitleMedium';
import { theme } from 'v3/styles/global-styles';

export const OnlineContractingTable = () => {
  const navigate = useNavigate();
  const { filter } = useStoreSelector((store) => store.onlineContracting);
  const { rowsPerPage, page } = filter;
  const { items, count } = useStoreSelector((store) => store.onlineContracting.list);
  const { isDesktop } = useBreakpoints();

  const handleNavigation = (contractingKey: string, origin?: string) => {
    const slug = getOriginSlug(origin);
    navigate(
      slug ?
        `${RoutesEnum.ContractingOnline}/${slug}/${contractingKey}`
        : `${RoutesEnum.ContractingOnline}/${contractingKey}`,
    );
  };

  return (
    <Fragment>
      {items.isEmpty ? 
        <NoItemsFound />
        : isDesktop ? 
          <PaperStyled>
            <TableContainer sx={{ maxHeight: 450, overflowX: 'auto' }}>
              <TableMui stickyHeader aria-label="sticky tables" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <OnlineTableCell minWidth="11rem" width="30%">
                      <TitleMedium>ID do Pedido</TitleMedium>
                    </OnlineTableCell>
                    <OnlineTableCell minWidth="9rem" width="28%">
                      <TitleMedium>Cliente</TitleMedium>
                    </OnlineTableCell>
                    <OnlineTableCell minWidth="6rem" width="15%">
                      <TitleMedium>Canal</TitleMedium>
                    </OnlineTableCell>
                    <OnlineTableCell minWidth="12rem" width="27%">
                      <TitleMedium>Financiamento</TitleMedium>
                    </OnlineTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => {
                    return (
                      <TableRowStyled
                        key={item.contractingKey}
                        onClick={() => handleNavigation(item.contractingKey, item.creditSimulation.origin)}
                      >
                        <OnlineTableCellGroup>
                          <SubtitleMedium className="empty-line">
                            {item.creditSimulation.vtexOrderId ? item.creditSimulation.vtexOrderId : ''}
                          </SubtitleMedium>
                        </OnlineTableCellGroup>
                        <OnlineTableCellGroup>
                          <SubtitleMedium>{item.person.fullName}</SubtitleMedium>
                          <SubtitleMedium color={theme.palette.secondary.main}>
                            {formatCPF(item.person.cpf)}
                          </SubtitleMedium>
                        </OnlineTableCellGroup>
                        <OnlineTableCellGroup>
                          <SubtitleMedium>{getChannelLabel(item.creditSimulation.origin)}</SubtitleMedium>
                        </OnlineTableCellGroup>
                        <StatusTableCellGroup>
                          <FinancingStatus contractingState={item.status} />
                        </StatusTableCellGroup>
                      </TableRowStyled>
                    );
                  })}
                </TableBody>
              </TableMui>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={ROWS_PER_PAGE_OPTION}
              component="div"
              sx={TablePaginationStyles}
              count={count}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, value) => {
                setFilterCurrentPage(value);
              }}
              onRowsPerPageChange={(event) => {
                setFilterCurrentPage(PAGE_INITIAL_VALUE);
                setFilterRowsPerPage(Number(event.target.value));
              }}
            />
          </PaperStyled>
          :         <OnlineContractingMobileTable contractingList={items} />
      }
    </Fragment>
  );
};
